import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  ButtonBase,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Bolt, ChevronRight, Close, History } from '@mui/icons-material';
import { getSimRun, listSimRuns, SimRunSummary, SimulationRun } from '../services/api';
import { GroupedHistoryList } from './GroupedHistoryList';

export const fmtRunTime = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

interface SimHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called with the saved run (exactly as returned at the time) and when it was made. */
  onOpenRun: (run: SimulationRun, createdAt: string) => void;
}

/** "Previous simulations": the signed-in user's saved Simulator runs, newest first. */
export const SimHistoryDialog: React.FC<SimHistoryDialogProps> = ({ open, onClose, onOpenRun }) => {
  const [runs, setRuns] = useState<SimRunSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [opening, setOpening] = useState<string | null>(null);
  const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'));

  useEffect(() => {
    if (!open) return;
    setRuns(null);
    setError(null);
    listSimRuns()
      .then(setRuns)
      .catch((e) => setError((e as Error).message));
  }, [open]);

  const openRun = async (runId: string) => {
    setOpening(runId);
    setError(null);
    try {
      const saved = await getSimRun(runId);
      onOpenRun(saved.run, saved.createdAt);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setOpening(null);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: { borderRadius: 2, backgroundImage: 'none', border: 1, borderColor: 'divider' } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <History color="primary" />
        <Box sx={{ flex: 1 }}>
          Previous simulations
          <Typography variant="body2" color="text.secondary">
            Reopen a run exactly as it came out at the time
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Close">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          {error && <Alert severity="error">{error}</Alert>}
          {runs === null && !error ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : runs && runs.length === 0 ? (
            <Alert severity="info">No saved simulations yet. Runs you make while signed in show up here.</Alert>
          ) : (
            <GroupedHistoryList
              items={runs ?? []}
              groupKey={(r) => r.site}
              itemKey={(r) => r.runId}
              renderItem={(r) => (
                <ButtonBase
                  onClick={() => openRun(r.runId)}
                  disabled={!!opening}
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    gap: 1.5,
                    p: 1.5,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {r.site}
                      </Typography>
                      {/* The race chaos the run used (runs from before race chaos were Normal). */}
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={r.chaos ? <Bolt /> : undefined}
                        color={r.chaos === 'wild' ? 'warning' : r.chaos === 'calm' ? 'info' : 'default'}
                        label={r.chaos === 'wild' ? 'Wild' : r.chaos === 'calm' ? 'Calm' : 'Normal'}
                        sx={{ height: 20, fontSize: 11, '& .MuiChip-icon': { fontSize: 14 } }}
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {fmtRunTime(r.createdAt)} · {r.nSims.toLocaleString()} simulations
                      {r.excludedTracks.length > 0
                        ? ` · ${r.excludedTracks.length} track${r.excludedTracks.length === 1 ? '' : 's'} removed`
                        : ''}
                    </Typography>
                  </Box>
                  {opening === r.runId ? <CircularProgress size={18} /> : <ChevronRight color="action" />}
                </ButtonBase>
              )}
            />
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
